import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { Box, LinearProgress, Skeleton, Stack } from '@mui/material';

import { Page, Button, EditIcon, IPaginationProps } from '@occc-app/design-system';
import {
  RetailerNetworkLocationGroupFragment,
  RetailerNetworkLocationFragment,
} from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import { UpdateRetailerNetworkLocationGroups } from '@occc-app/App/PolicyBasedAccessControl/userActions';

import { GroupLocationsTable } from './components/retailer-network-group-locations-table.component';

import { StoreMapSection } from '../retailer-network-my-stores-v2-page/components/store-map-section.component';
import { LocationGroupUpdateDialog } from '../components/location-group-update-dialog/location-group-update-dialog.component';
import { RetailerNetworkLocationGroupAddLocationsDialog } from '../retailer-network-location-group-details-page/components/retailer-network-location-group-add-location-dialog.component';
import { RetailerNetworkLocationGroupRemoveLocationDialog } from '../retailer-network-location-group-details-page/components/retailer-network-location-group-remove-location-dialog.component';
import RetailerNetworkLocationGroupUsersTable from '../retailer-network-location-group-details-page/components/retailer-network-location-group-users-table';
import { withRetailerNetwork, WithRetailerNetworkProps } from '../hocs/with-retailer-network.hoc';
import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';
import { routeCreator } from '../retailer-connect.utils';

const USERS_TABLE_TITLE = 'Location Group Admins and Users';

export type RetailerNetworkMyStoresGroupV2PageProps = WithRetailerNetworkProps & {
  locationGroup: RetailerNetworkLocationGroupFragment;
  allLocations: RetailerNetworkLocationFragment[];
  isAllLocationsLoading: boolean;
  tableLocations: RetailerNetworkLocationFragment[];
  areTableLocationsLoading: boolean;
  tableLocationsError?: ApolloError;
  tablePaginationProps: IPaginationProps;
  tableSearch: string | undefined;
  onTableSearch: (search: string | undefined) => void;
  addLocationsToLocationGroup: (locationUuids: string[]) => Promise<void>;
  removeLocationFromLocationGroup: (locationUuid: string) => Promise<void>;
  isAddLocationToLocationGroupInProgress: boolean;
  isRemoveLocationFromGroupInProgress: boolean;
};

export const RetailerNetworkMyStoresGroupV2PageComponent = ({
  retailerNetwork,
  locationGroup,
  allLocations,
  isAllLocationsLoading,
  tableLocations,
  areTableLocationsLoading,
  tableLocationsError,
  tablePaginationProps,
  tableSearch,
  onTableSearch,
  addLocationsToLocationGroup,
  removeLocationFromLocationGroup,
  isAddLocationToLocationGroupInProgress,
  isRemoveLocationFromGroupInProgress,
}: RetailerNetworkMyStoresGroupV2PageProps) => {
  const navigate = useNavigate();
  const networkUuid = retailerNetwork.uuid;
  const { isRetailerNetworkSiteUser, isRetailerNetworkSiteUserRole, retailerNetworkUserUuid, retailerNetworkUserOpenCommerceUserUuid } =
    useRetailerNetworkUserSelector();

  const userScopingFilter =
    isRetailerNetworkSiteUserRole && retailerNetworkUserUuid
      ? {
          retailerNetworkUserUuidEquals: retailerNetworkUserUuid,
          openCommerceUserUuidEquals: retailerNetworkUserOpenCommerceUserUuid ?? undefined,
        }
      : {};

  const [isEditDetailsOpen, setEditDetailsOpen] = useState(false);
  const [isAddLocationsOpen, setAddLocationsOpen] = useState(false);
  const [removeLocationUuid, setRemoveLocationUuid] = useState<string | null>(null);

  // Both "View Store Details" and a row click open the Store Details page, carrying groupRef so the
  // breadcrumb there can navigate back to this group.
  const handleViewStoreDetails = useCallback(
    (location: RetailerNetworkLocationFragment) => {
      navigate(
        `${routeCreator.locationDetails(networkUuid, location.uuid)}?groupRef=${
          locationGroup.uuid
        }&groupName=${encodeURIComponent(locationGroup.name)}`,
      );
    },
    [navigate, networkUuid, locationGroup.uuid, locationGroup.name],
  );

  // "Edit Store" reuses the existing locations configuration page, pre-filtered to the store's Site ID.
  const handleEditStore = useCallback(
    (location: RetailerNetworkLocationFragment) => {
      navigate(routeCreator.locationUpdate(networkUuid, location.uuid));
    },
    [navigate, networkUuid],
  );

  const handleConfirmRemoveLocation = async () => {
    if (removeLocationUuid) {
      await removeLocationFromLocationGroup(removeLocationUuid);
    }
    setRemoveLocationUuid(null);
  };

  const handleAddLocations = async (locationUuids: string[]) => {
    await addLocationsToLocationGroup(locationUuids);
    setAddLocationsOpen(false);
  };

  return (
    <Page
      browserTabTitle={locationGroup.name}
      pageHeaderProps={{
        title: locationGroup.name,
        titleTestId: 'rc-group-v2-title',
        subtitle: 'Location Group',
      }}
      containerProps={{ variant: 'md' }}
      actions={
        <Can I={UpdateRetailerNetworkLocationGroups({ uuid: networkUuid })}>
          <Button
            variant='contained'
            color='secondary'
            startIcon={<EditIcon />}
            onClick={() => setEditDetailsOpen(true)}
            data-testid='rc-group-edit-details'
          >
            Edit Details
          </Button>
        </Can>
      }
    >
      <Stack spacing={1} mt={2}>
        {isAllLocationsLoading && allLocations.length === 0 ? (
          <Box>
            <LinearProgress />
            <Skeleton variant='rectangular' height={300} />
          </Box>
        ) : allLocations.length > 0 ? (
          <StoreMapSection
            boundsLocations={allLocations}
            pinLocations={allLocations}
            selectedLocationUuid={null}
            onPinClick={(locationUuid) => {
              const location = allLocations.find((item) => item.uuid === locationUuid);
              if (location) handleViewStoreDetails(location);
            }}
          />
        ) : null}

        <GroupLocationsTable
          retailerNetworkUuid={networkUuid}
          locations={tableLocations}
          loading={areTableLocationsLoading}
          error={tableLocationsError}
          search={tableSearch}
          onSearch={onTableSearch}
          paginationProps={tablePaginationProps}
          onAddLocation={() => setAddLocationsOpen(true)}
          onViewStoreDetails={handleViewStoreDetails}
          onEditStore={handleEditStore}
          onRemoveLocation={setRemoveLocationUuid}
        />

        {!isRetailerNetworkSiteUser && (
          <RetailerNetworkLocationGroupUsersTable
            retailerNetworkUuid={networkUuid}
            locationGroup={locationGroup}
            title={USERS_TABLE_TITLE}
            searchParamName='users-search'
            beforeParamName='users-before'
            afterParamName='users-after'
            tableHeaderSx={{ '& h2': { fontSize: '1.6rem', fontWeight: 500 } }}
          />
        )}
      </Stack>

      {isEditDetailsOpen && (
        <LocationGroupUpdateDialog
          locationGroup={locationGroup}
          open={isEditDetailsOpen}
          onClose={() => setEditDetailsOpen(false)}
          retailerNetworkUuid={networkUuid}
        />
      )}

      {isAddLocationsOpen && (
        <RetailerNetworkLocationGroupAddLocationsDialog
          open={isAddLocationsOpen}
          setOpen={setAddLocationsOpen}
          filter={{ retailerNetworkUuidEquals: networkUuid, ...userScopingFilter }}
          onAddLocationsToLocationGroup={handleAddLocations}
          loading={isAddLocationToLocationGroupInProgress}
          retailerNetworkLocationGroupUuid={locationGroup.uuid}
        />
      )}

      {removeLocationUuid !== null && (
        <RetailerNetworkLocationGroupRemoveLocationDialog
          open={removeLocationUuid !== null}
          setOpen={(value) => !value && setRemoveLocationUuid(null)}
          onConfirm={handleConfirmRemoveLocation}
          loading={isRemoveLocationFromGroupInProgress}
        />
      )}
    </Page>
  );
};

export const RetailerNetworkMyStoresGroupV2Page = withRetailerNetwork(
  RetailerNetworkMyStoresGroupV2PageComponent,
);
